(() => {
  if (window.CanteinIngredientChecker) return;
  window.CanteinIngredientChecker = true;

  const LINK = {
    sucralose: '/pages/protein-powder-without-sucralose',
    artificial: '/pages/protein-powder-without-artificial-sweeteners',
    stevia: '/pages/protein-powder-without-stevia',
    dairy: '/pages/best-dairy-free-protein-powder',
    bloat: '/pages/why-protein-powder-bloats-you',
    acacia: '/pages/acacia-fiber',
    pea: '/pages/pea-protein-alternatives',
    lead: '/pages/lead-in-protein-powder',
  };
  const a = (href, text) => `<a href="${href}">${text}</a>`;

  const GROUPS = {
    artificial: {
      title: 'Artificial sweeteners',
      tone: 'flag',
      note: `The FDA has approved six high-intensity sweeteners as food additives: saccharin, aspartame, acesulfame potassium, sucralose, neotame and advantame. It considers them safe within set daily limits. ${a(LINK.sucralose, 'Protein powders without sucralose')}.`,
    },
    stevia: {
      title: 'Stevia',
      tone: 'flag',
      note: `Purified stevia extracts are "generally recognized as safe" in the US. The usual complaint is a bitter, licorice-like aftertaste. ${a(LINK.stevia, 'Protein powders without stevia')}.`,
    },
    polyol: {
      title: 'Sugar alcohols',
      tone: 'flag',
      note: `Sugar alcohols are only partly absorbed. A systematic review found dose-dependent gas, discomfort and laxative effects, in healthy people and in people with IBS. ${a(LINK.artificial, 'More on sweeteners and your stomach')}.`,
    },
    othersweet: {
      title: 'Other low-calorie sweeteners',
      tone: 'info',
      note: 'Low-calorie sweeteners that are neither artificial sweeteners nor sugar alcohols.',
    },
    sugar: {
      title: 'Added sugars',
      tone: 'flag',
      note: 'Check "Added Sugars" on the nutrition panel for the grams per scoop. For scale, a teaspoon of sugar is about 4g.',
    },
    monk: {
      title: 'Monk fruit',
      tone: 'info',
      note: `Monk fruit extract is "generally recognized as safe" and 100 to 250 times sweeter than sugar. It isn't stevia or an artificial sweetener. ${a(LINK.artificial, 'How each sweetener is regulated')}.`,
    },
    dairy: {
      title: 'Dairy',
      tone: 'flag',
      note: `Milk is one of the nine major food allergens under US law. Whey isolate is still milk, so it isn't dairy-free. ${a(LINK.dairy, 'Dairy-free protein powders, compared')}.`,
    },
    soy: {
      title: 'Soy',
      tone: 'flag',
      note: 'Soy is one of the nine major food allergens under US law. Soy lecithin is used in small amounts to help powder mix.',
    },
    allergen: {
      title: 'Other major allergens',
      tone: 'flag',
      note: 'US law names nine major food allergens: milk, eggs, fish, crustacean shellfish, tree nuts, peanuts, wheat, soybeans and sesame.',
    },
    legume: {
      title: 'Pea and other legumes',
      tone: 'info',
      note: `Legumes carry fermentable carbohydrates; isolates carry less. If beans bother you, a legume protein might too. ${a(LINK.pea, 'Pea protein alternatives')}.`,
    },
    gum: {
      title: 'Gums and thickeners',
      tone: 'info',
      note: `Added in small amounts for a thicker shake. If shakes upset your stomach, a powder without them is a fair comparison. ${a(LINK.bloat, 'Why protein powder bloats you')}.`,
    },
    fiber: {
      title: 'Added fiber',
      tone: 'info',
      note: `Gut bacteria ferment most added fibers. Chicory inulin raised gas at common doses in a tolerance study; acacia was well tolerated in its studies. ${a(LINK.bloat, 'Why protein powder bloats you')} · ${a(LINK.acacia, 'Acacia fiber')}.`,
    },
    coconut: {
      title: 'Coconut or MCT',
      tone: 'info',
      note: 'MCT (medium-chain triglyceride) powder is usually made from coconut or palm oil.',
    },
    color: {
      title: 'Added colors',
      tone: 'flag',
      note: 'The FDA requires certified colors such as Yellow 5 to be named on the label.',
    },
    flavor: {
      title: 'Flavors',
      tone: 'info',
      note: '"Natural flavor" is an FDA term for extracts of plants, meat, seafood, eggs or dairy whose job is flavor, not nutrition. The source doesn\'t have to be named, but a major allergen in a flavor still has to be declared.',
    },
    filler: {
      title: 'Starches and carbs',
      tone: 'info',
      note: 'Maltodextrin and starches add carbohydrate and bulk. They are often carriers for flavors or oils.',
    },
  };
  const ORDER = Object.keys(GROUPS);

  const R = (group, re, not, tag) => ({ group, re, not, tag });
  const RULES = [
    R('artificial', /\bsucralose\b|\bsplenda\b|\bacesulfame\b|\bace[\s-]?k\b|\baspartame\b|\bsaccharin\b|\bneotame\b|\badvantame\b/),
    R('stevia', /\bstevi(a|ol)|\brebaudioside|\breb[\s-]?[adm]\b/),
    R('monk', /\bmonk\s?fruit|\blu?o\s?han|\bsiraitia|\bmogrosides?\b/),
    R('polyol', /\b(erythritol|xylitol|maltitol|sorbitol|mannitol|isomalt|lactitol)\b/),
    R('othersweet', /\b(allulose|psicose|tagatose|thaumatin)\b/),
    R(
      'sugar',
      /\bsugars?\b|\b(sucrose|dextrose|fructose|glucose|honey|molasses)\b|\bsyrups?\b|\bcane juice\b|\bjuice concentrate\b|\bagave\b(?!\s*(inulin|fib))/,
      /\bsugar alcohol|\bsugar[\s-]?free|\bno (added )?sugar|\bfib(er|re) syrup/
    ),
    R(
      'dairy',
      /\bwhey\b|\bcasein|\bcaseinate|\bmilk\b|\blactose\b|\blact(albumin|oglobulin)|\bbutter(milk)?\b|\bcream\b|\bcheese\b|\byogh?urt\b|\bghee\b|\bcolostrum\b/,
      /\b(coconut|almond|oat|soy|rice|cashew|hemp|pea|nut|seed|cocoa|cacao|shea|peanut|sunflower)\s+(milk|cream|butter)\b|\bbutternut|\bcream of tartar|\bnon[\s-]?dairy|\bmilk thistle|\b(milk|dairy|lactose)[\s-]?free/
    ),
    R('soy', /\bsoy|\bedamame\b/, /\bsoy[\s-]?free/),
    R('allergen', /\beggs?\b|\balbumen\b|\bovalbumin\b/, /\begg[\s-]?free/, 'egg'),
    R('allergen', /\bwheat\b|\bwheatgrass\b|\bgluten\b|\bsemolina\b|\bspelt\b|\bdurum\b|\bfarina\b/, /\b(gluten|wheat)[\s-]?free/, 'wheat'),
    R('allergen', /\bpeanuts?\b|\bgroundnuts?\b|\barachis\b/, /\bpeanut[\s-]?free/, 'peanut'),
    R('allergen', /\b(almonds?|cashews?|walnuts?|pecans?|pistachios?|hazelnuts?|macadamias?|filberts?)\b|\bbrazil nuts?\b|\bpine nuts?\b/, null, 'tree nut'),
    R('allergen', /\bsesame\b|\btahini\b/, null, 'sesame'),
    R('allergen', /\bfish\b|\banchov|\b(salmon|tuna|cod|tilapia|pollock|sardines?)\b/, null, 'fish'),
    R('allergen', /\b(shrimp|crabs?|lobsters?|krill|prawns?)\b|\bcrustacean/, null, 'shellfish'),
    R('legume', /\bpeas?\b|\bchick\s?peas?\b|\bgarbanzo|\blentils?\b|\b(fava|faba|mung)\b|\blupin|\b(navy|black|kidney|pinto|broad|adzuki)\s+beans?\b/),
    R(
      'gum',
      /\bgums?\b|\b(carrageenan|gellan|xanthan|guar|pectin|agar|konjac|glucomannan)\b|\blocust bean\b|\bcarob bean\b|\bcarboxymethyl|\bmethylcellulose\b|\balginate\b|\btara\b/,
      /\bgum[\s-]?free/
    ),
    R(
      'fiber',
      /\b(inulin|chicory|oligofructose|polydextrose|acacia|psyllium|baobab|phgg|cellulose|glucomannan|konjac)\b|\bfructo[\s-]?oligosaccharides?\b|\bgalacto[\s-]?oligosaccharides?\b|\bxylo[\s-]?oligosaccharides?\b|\b(fos|gos)\b|\bcorn fiber\b|\bresistant (dextrin|starch|maltodextrin)\b|\bgum arabic\b|\bprebiotic|\bfib(er|re)s?\b|\bpartially hydrolyzed guar\b/,
      /\bcellulose gum\b|\bfib(er|re)[\s-]?free/
    ),
    R('coconut', /\bcoconut\b|\bmct\b|\bmedium[\s-]chain/),
    R(
      'color',
      /\b(red|yellow|blue|green)\s*(no\.?\s*)?\d+\b|\bfd\s?&\s?c\b|\blakes?\b|\bcaramel colou?r|\btitanium dioxide\b|\bcarmine\b|\bcochineal\b|\bannatto\b|\bcolou?rs?\b|\bcolou?ring\b/
    ),
    R('flavor', /\bflavou?rs?\b|\bflavou?ring\b/),
    R('filler', /\bmaltodextrin\b|\bcorn syrup solids\b|\bdextrin\b|\bstarch\b|\brice flour\b|\btapioca\b/, /\bresistant\b/),
  ];

  const PROTEINS = [
    ['whey', /\bwhey\b/],
    ['casein', /\bcasein|\bcaseinate/],
    ['milk protein', /\bmilk protein/],
    ['egg white', /\begg white|\balbumen\b/],
    ['soy', /\bsoy protein|\bsoy isolate/],
    ['pea', /\bpea protein|\bpeas? isolate|\byellow peas?\b/],
    ['rice', /\brice protein/],
    ['hemp', /\bhemp\b/],
    ['pumpkin seed', /\bpumpkin/],
    ['sunflower seed', /\bsunflower (seed )?protein|\bsunflower seeds? $/],
    ['chia', /\bchia\b/],
    ['quinoa', /\bquinoa\b/],
    ['collagen', /\bcollagen|\bgelatin/],
    ['beef', /\bbeef\b|\bbone broth/],
    ['fava bean', /\b(fava|faba)\b/],
    ['mung bean', /\bmung\b/],
    ['chickpea', /\bchick\s?pea|\bgarbanzo/],
    ['almond', /\balmond protein/],
    ['watermelon seed', /\bwatermelon seed/],
    ['sacha inchi', /\bsacha inchi/],
    ['potato', /\bpotato protein/],
    ['oat', /\boat protein/],
    ['lentil', /\blentil protein/],
    ['cricket', /\bcricket/],
    ['mycoprotein', /\bmycoprotein|\bmycelium/],
    ['algae', /\bspirulina|\balgae protein|\bchlorella/],
  ];

  const CHECKS = [
    { label: 'Sucralose', test: (h) => h.group === 'artificial' && /sucralose|splenda/.test(h.norm) },
    { label: 'Other artificial sweeteners', test: (h) => h.group === 'artificial' && !/sucralose|splenda/.test(h.norm) },
    { label: 'Stevia', test: (h) => h.group === 'stevia' },
    { label: 'Sugar alcohols', test: (h) => h.group === 'polyol' },
    { label: 'Added sugar', test: (h) => h.group === 'sugar' },
    { label: 'Dairy', test: (h) => h.group === 'dairy' },
    { label: 'Soy', test: (h) => h.group === 'soy' },
    { label: 'Pea or legumes', test: (h) => h.group === 'legume' },
    { label: 'Gums', test: (h) => h.group === 'gum' },
    { label: 'Added fiber', test: (h) => h.group === 'fiber' },
    { label: 'Added colors', test: (h) => h.group === 'color' },
  ];

  const STATEMENT_ALLERGENS = [
    ['dairy', /\bmilk|\bdairy/, 'milk'],
    ['soy', /\bsoy/, 'soy'],
    ['allergen', /\beggs?\b/, 'egg'],
    ['allergen', /\bwheat/, 'wheat'],
    ['allergen', /\bpeanut/, 'peanut'],
    ['allergen', /\btree nuts?|\balmond|\bcashew|\bwalnut|\bpecan|\bpistachio|\bhazelnut|\bmacadamia/, 'tree nut'],
    ['coconut', /\bcoconut/, 'coconut'],
    ['allergen', /\bsesame/, 'sesame'],
    ['allergen', /\bfish\b/, 'fish'],
    ['allergen', /\bshellfish|\bcrustacean/, 'shellfish'],
  ];

  const I = (re, why, watch = '') => ({ re, why, watch });
  const LEGUME_WATCH = `A legume. If beans or peas bother you, it might too. ${a(LINK.pea, 'Pea protein alternatives')}.`;
  const SUGAR_WATCH = 'Counts toward Added Sugars on the label. A teaspoon of sugar is about 4g.';
  const FLAVOR_WATCH = "The label doesn't have to say what's in a flavor or where it comes from, but a major allergen in it still has to be declared.";
  const INFO = [
    I(/\bhydrolyz(ed|ate)\b[^,]*\bwhey|\bwhey protein hydrolys|\bwhey peptides?\b/, 'Whey that has been partly broken down with enzymes into smaller pieces (peptides).', 'Still a milk ingredient, so not for a milk allergy. Hydrolyzed whey can taste more bitter than regular whey.'),
    I(/\bwhey (protein )?isolate/, 'Milk protein filtered to remove most of the fat and lactose, so it is at least 90% protein.', `Still a milk ingredient, so not for a milk allergy. Usually lower in lactose than whey concentrate. ${a(LINK.dairy, 'Dairy-free options')}.`),
    I(/\bwhey\b/, 'Milk protein with more of its natural fat and lactose left in than isolate.', `A milk ingredient. It carries more lactose than isolate, which matters if you're lactose intolerant. ${a(LINK.dairy, 'Dairy-free options')}.`),
    I(/\bcasein|\bcaseinate/, 'The main protein in milk. It forms curds in the stomach, so it digests more slowly than whey.', 'A milk ingredient.'),
    I(/\bmilk protein/, 'Concentrated milk protein, mostly casein with some whey.', 'A milk ingredient.'),
    I(/\begg white|\balbumen\b/, 'Dried egg white protein.', 'Egg is one of the nine major US food allergens.'),
    I(/\bsoy protein|\bsoy isolate/, 'Protein from soybeans, one of the few plant proteins with all nine essential amino acids in good amounts.', 'Soy is one of the nine major US food allergens.'),
    I(/\bpea protein|\bpeas? isolate|^ pea |\byellow peas?\b/, 'Protein extracted from yellow peas.', `Lower in methionine than animal proteins, which is why it's often blended with rice. ${LEGUME_WATCH}`),
    I(/\brice protein/, 'Protein extracted from brown rice, often paired with pea to round out the amino acids.', `Rice can take up arsenic from soil and water, so it's worth asking the brand for heavy-metal results. ${a(LINK.lead, 'Lead in protein powder')}.`),
    I(/\bhemp\b/, 'Protein from pressed hemp seeds. It is less refined than most isolates, so it keeps some fiber and fat.', 'Lysine is its limiting amino acid. It has an earthy taste that some people notice.'),
    I(/\bpumpkin/, 'Pumpkin seed or pumpkin seed protein, usually blended with other plant proteins.', "Not one of the nine major US food allergens."),
    I(/\bsunflower (seed )?protein|\bsunflower seeds? $/, 'Protein from pressed sunflower seeds.', "Not one of the nine major US food allergens."),
    I(/\bchia\b/, 'Chia seed or chia protein, adding some protein, fiber and fat.'),
    I(/\bcollagen|\bgelatin/, 'Protein from animal hide, bone or fish skin.', "Not a complete protein: collagen has no tryptophan."),
    I(/\bchick\s?peas?\b|\bgarbanzo|\blentil|\b(fava|faba|mung|adzuki)\b|\blupin/, 'A legume protein.', LEGUME_WATCH),
    I(/\bpeanut/, 'Ground roasted peanuts, for protein and flavor.', 'Peanut is one of the nine major US food allergens.'),
    I(/\balmond/, 'Protein from pressed almonds.', 'Almonds are tree nuts, one of the nine major US food allergens.'),
    I(/\bsesame\b/, 'Sesame seed or sesame protein.', 'Sesame is one of the nine major US food allergens.'),
    I(/\bsacha inchi/, 'Protein from the seeds of sacha inchi, a Peruvian plant.'),
    I(/\bnutritional yeast\b/, 'Protein from nutritional yeast, a deactivated yeast grown for food.'),
    I(/\bleucine\b/, 'Leucine, an essential amino acid, added on top of the protein.'),
    I(/\bcranberry seed/, 'Ground cranberry seed, which adds some protein, fiber and fat.'),
    I(/\bspirulina\b/, 'A blue-green alga, dried and powdered.'),
    I(/\bchlorella\b/, 'A freshwater alga, dried and powdered. "Cracked wall" means its tough cell wall was broken open during processing.'),
    I(/\bflax/, 'Ground flaxseed, which adds some fiber and plant omega-3 fat.'),
    I(/\bsprout(s|ed)?\b/, 'A seed, grain or bean that was sprouted, then dried and ground into the protein blend.'),
    I(/\btapioca flour\b/, 'A starch, for bulk and texture.', GROUPS.filler.note),
    I(/\bflour\b/, 'A flour, here part of mixed-in baked pieces.'),
    I(/\b(quinoa|amaranth|buckwheat|millet|sorghum)\b/, 'A seed or grain, ground into the powder for some extra protein and fiber.'),

    I(/\bsucralose\b|\bsplenda\b/, 'An artificial sweetener about 600 times sweeter than sugar, with no calories.', `The FDA approved it within an acceptable daily intake. A 2022 study in <em>Cell</em> found it changed gut bacteria and blood sugar responses in some people. ${a(LINK.sucralose, 'Powders without sucralose')}.`),
    I(/\bacesulfame\b|\bace[\s-]?k\b/, 'An artificial sweetener about 200 times sweeter than sugar, often paired with sucralose for a more sugar-like taste.', `FDA-approved within an acceptable daily intake. It's usually blended with another sweetener rather than used alone. ${a(LINK.artificial, 'Powders without artificial sweeteners')}.`),
    I(/\baspartame\b/, 'An artificial sweetener about 200 times sweeter than sugar.', 'Labels must warn people with phenylketonuria (PKU) because it contains phenylalanine. In 2023 the WHO\'s cancer agency listed it as "possibly carcinogenic to humans"; the WHO food additives committee kept its daily limit, and the FDA still considers it safe at approved levels.'),
    I(/\bsaccharin\b/, 'An artificial sweetener 200 to 700 times sweeter than sugar.', 'FDA-approved. The oldest of the common artificial sweeteners.'),
    I(/\bneotame\b|\badvantame\b/, 'An artificial sweetener thousands of times sweeter than sugar, so very little is used.', 'FDA-approved.'),
    I(/\bstevi(a|ol)|\brebaudioside|\breb[\s-]?[adm]\b/, 'Sweet compounds purified from stevia leaves, 200 to 400 times sweeter than sugar, with no calories.', `Generally recognized as safe in the US. Some people taste a bitter or licorice-like aftertaste; Reb M is a newer type that tastes closer to sugar. ${a(LINK.stevia, 'Powders without stevia')}.`),
    I(/\bmonk\s?fruit|\blu?o\s?han|\bsiraitia|\bmogrosides?\b/, 'Extract of luo han guo, a small melon-like fruit. Its sweet compounds (mogrosides) are 100 to 250 times sweeter than sugar.', 'Generally recognized as safe in the US. Retail "monk fruit sweeteners" are often mostly erythritol, so check what else is on the list.'),
    I(/\berythritol\b/, 'A sugar alcohol with almost no calories, used for sweetness and bulk.', 'Better tolerated than most sugar alcohols because most of it is absorbed before the colon, but large amounts can still upset your stomach.'),
    I(/\bxylitol\b/, 'A sugar alcohol with fewer calories than sugar.', 'Larger amounts can cause gas and a laxative effect. Xylitol is toxic to dogs, so keep the tub out of reach.'),
    I(/\b(maltitol|sorbitol|mannitol|isomalt|lactitol)\b/, 'A sugar alcohol used for sweetness and bulk.', `Only partly absorbed, so larger amounts can cause gas, bloating and a laxative effect. ${a(LINK.artificial, 'More on sweeteners and your stomach')}.`),
    I(/\ballulose\b|\bpsicose\b/, 'A rare sugar that tastes like sugar but has very few calories.', 'The FDA lets brands leave it out of Total and Added Sugars. Large amounts can upset your stomach.'),
    I(/\btapioca fib|\bisomalto|\bimo\b/, 'Isomaltooligosaccharides (IMO): a carbohydrate made from tapioca starch, sold as a fiber syrup or powder.', 'Part of it can be digested like a starch, so it may not all act like fiber.'),
    I(/\bmct\b|\bmedium[\s-]chain/, 'Fat from coconut or palm oil, used as a powder for a creamier shake.', 'Adds a little fat. Large amounts of MCT oil can upset your stomach.'),
    I(/\bcoconut (palm )?sugar\b/, 'Sugar made from coconut palm sap.', `It's still added sugar. ${SUGAR_WATCH}`),
    I(/\bmolasses\b/, 'A dark syrup left over from refining sugar, used for a caramel flavor.', SUGAR_WATCH),
    I(/\bcorn syrup solids\b/, 'Dried corn syrup, used as a sweetener and to carry oils in a powdered creamer.', SUGAR_WATCH),
    I(/\bsugars?\b|\bsucrose\b/, 'Sugar, for sweetness.', SUGAR_WATCH),
    I(/\b(honey|dextrose|fructose|glucose)\b|\bagave\b(?!\s*(inulin|fib))|\bsyrups?\b|\bcane juice\b|\bjuice concentrate\b/, 'A sugar or syrup, for sweetness.', SUGAR_WATCH),
    I(/\bmaltodextrin\b/, 'A starch-based carbohydrate used as a filler or to carry flavors and oils.', "Adds carbohydrate with little else. It isn't counted as sugar on the label."),
    I(/\brice dextrin\b/, 'A carbohydrate made from rice starch, often used to turn an oil into a powder.', 'Adds a little carbohydrate.'),

    I(/\blecithin \([^)]*,|\b(and\/or|or)\b[^,]*\blecithin\b/, 'An emulsifier that helps the powder mix without clumping. The label lists more than one possible source.', "If soy is one of them, treat it as a soy ingredient; soy is a major allergen."),
    I(/\bsoy lecithin|\blecithin \(soy\)/, 'An emulsifier from soybeans that helps the powder mix without clumping.', "Soy is a major allergen, so it's declared even though only a small amount is used."),
    I(/\bsunflower lecithin|\blecithin \(sunflower\)/, 'An emulsifier from sunflower seeds that helps the powder mix without clumping.', 'A common soy-free alternative to soy lecithin.'),
    I(/\blecithin\b/, 'An emulsifier that helps the powder mix without clumping.', "Check the source: soy lecithin is a soy allergen, sunflower lecithin isn't."),
    I(/\bxanthan\b/, 'A thickener made by fermenting sugar with bacteria. It gives a thicker, smoother shake.', `Used in small amounts. If shakes bother your stomach, compare a powder without gums. ${a(LINK.bloat, 'Why protein powder bloats you')}.`),
    I(/\bguar\b/, 'A thickener from guar beans.', `Gut bacteria ferment it, which can mean gas for some people. ${a(LINK.bloat, 'Why protein powder bloats you')}.`),
    I(/\bcellulose gum\b|\bcarboxymethyl/, 'A thickener made from plant cellulose, also called carboxymethylcellulose (CMC).', 'Not digested. Used in small amounts for texture.'),
    I(/\bcarrageenan\b/, 'A seaweed extract that thickens a shake and keeps it from separating.', 'Some people avoid it because animal studies linked a degraded form to gut inflammation. The food-grade form is approved in the US.'),
    I(/\bgellan\b/, 'A thickener made by bacterial fermentation, used in very small amounts.'),
    I(/\bacacia\b|\bgum arabic\b/, 'A fiber from acacia tree sap, used as a thickener and to add fiber.', `It was well tolerated in digestive studies. ${a(LINK.acacia, 'Acacia fiber')}.`),
    I(/\binulin\b|\bchicory\b|\boligofructose\b|\bfructo[\s-]?oligosaccharides?\b|\bfos\b/, 'A prebiotic fiber, usually from chicory root or agave.', `Gut bacteria ferment it quickly. In a tolerance study it raised gas at common doses. ${a(LINK.bloat, 'Why protein powder bloats you')}.`),
    I(/\bcorn fiber\b|\bresistant dextrin\b|\bpolydextrose\b/, 'An added fiber, used for fiber and body with little sweetness.', 'Counts as dietary fiber on the label. Large amounts can cause gas in some people.'),
    I(/\bpsyllium\b/, 'A fiber from psyllium seed husks.', 'It thickens with water, so drink enough with it.'),
    I(/\bkonjac\b|\bglucomannan\b/, 'A thickener and fiber from konjac root.', 'Used in small amounts for texture.'),

    I(/\bnatural and artificial flavou?rs?\b/, 'A mix of flavorings from natural sources and ones made synthetically.', FLAVOR_WATCH),
    I(/\bartificial flavou?rs?\b/, 'Flavorings made synthetically rather than extracted from a natural source.', FLAVOR_WATCH),
    I(/^(?!.*\bflavou?r).*\bvanilla\b/, 'Vanilla, for flavor.'),
    I(/\bflavou?rs?\b|\bflavou?ring\b/, 'Flavor from a natural source such as a plant, dairy or fermentation.', FLAVOR_WATCH),
    I(/\bcocoa butter\b|\bchocolate\b/, 'Chocolate or cocoa butter, for chocolate flavor or mixed-in chocolate pieces.'),
    I(/\bcocoa\b|\bcacao\b/, 'Cocoa powder, for chocolate flavor. "Processed with alkali" (Dutch process) makes it darker and less bitter.', `Cocoa can contain lead and cadmium, so chocolate flavors are worth checking for lab results. ${a(LINK.lead, 'Lead in protein powder')}.`),
    I(/\bcoconut (cream|milk)\b/, 'Dried coconut milk or cream, for creaminess.', 'Adds saturated fat.'),
    I(/\bcreamer\b/, 'A powdered creamer, for a richer shake.', "A creamer is itself a mix of ingredients. If the label doesn't list them, ask the brand."),
    I(/\bdigestive (health )?blend\b/, 'A blend the brand adds to help digestion, often enzymes or probiotics.', "If the label doesn't list what's in it, ask the brand."),
    I(/\bmono[\s-]+and diglycerides\b|\bmono[\s-]?glycerides\b/, 'Emulsifiers made from fat, used to help oil and water mix.'),
    I(/\boils?\b/, 'A vegetable oil, for a creamier texture or as part of a mixed-in ingredient.', 'Adds a little fat.'),
    I(/\bpotassium chloride\b/, 'A mineral salt used for flavor and as an electrolyte.'),
    I(/\bsalt\b|\bsodium chloride\b/, 'Salt, to round out flavor.', 'Check Sodium on the nutrition panel.'),
    I(/\bbaking soda\b|\bsodium bicarbonate\b/, 'A leavening agent, here part of mixed-in baked pieces.'),
    I(/^ water $/, 'Water, used to make a mixed-in ingredient. It dries out during processing.'),
    I(/\bsilicon dioxide\b|\bsilica\b/, 'An anti-caking agent that keeps the powder from clumping.', 'FDA rules cap it at 2% of the food by weight.'),
    I(/\bcalcium silicate\b/, 'An anti-caking agent that keeps the powder from clumping.'),
    I(/\b(di|tri)(potassium|calcium|sodium|magnesium) phosphate\b/, 'A mineral salt used to stabilize the powder or creamer and stop it caking. It also adds a little of that mineral.'),
    I(/\brosemary\b/, 'Rosemary extract, an antioxidant used as a natural preservative to keep fats from going rancid.'),
    I(/\bd[\s-]alpha[\s-]tocopher/, 'Vitamin E, added as a nutrient.'),
    I(/\btocopherols?\b/, 'Forms of vitamin E, used as a natural preservative to keep fats from going rancid.'),
    I(/\blactase\b/, 'An enzyme that breaks down lactose, the sugar in milk, added to help digestion.', "It's an enzyme, not a milk ingredient."),
    I(/\bprohydrolase\b|\bproteases?\b|\benzymes?\b|\bbromelain\b|\bpapain\b|\bpeptidase\b|\baspergillopepsin\b/, 'Enzymes that break down protein, added by the brand to help digestion.'),
    I(/\b(amylases?|lipases?|cellulases?|hemicellulases?|xylanases?|pectinases?|phytases?)\b|\bglucanases?\b/, 'An enzyme that breaks down starch, fat or plant fiber, added by the brand to help digestion.'),
    I(/\bbacillus\b|\blactobacill|\bbifidobacteri|\bcfu\b|\bprobiotics?\b|\b(acidophilus|plantarum|longum|coagulans|subtilis)\b/, 'A probiotic: live bacteria added by the brand. CFU (colony-forming units) is the count of live bacteria.', 'Any effect depends on the exact strain and dose, so look for studies on that strain.'),
    I(/\bmushroom|\bshiitake\b|\breishi\b|\bcordyceps\b|\blion'?s mane\b|\bturkey tail\b/, 'A dried mushroom extract.'),
    I(/\bwheat ?grass\b/, 'Young wheat grass, dried and powdered.', "It comes from the wheat plant, so it's flagged as wheat here. Ask the brand about gluten testing if that matters to you."),
    I(/\bbarley grass\b|\balfalfa\b/, 'Young barley or alfalfa grass, dried and powdered.'),
    I(/\bmoringa\b/, 'Dried, powdered leaves of the moringa tree.'),
    I(/\bseaweed\b|\bkelp\b/, 'Dried seaweed, here a source of minerals such as calcium.'),
    I(/\byacon\b/, 'Dried yacon, a sweet root.'),
    I(/\bturmeric\b/, 'Turmeric root, for color and flavor.'),
    I(/\bcinnamon\b/, 'Cinnamon, for flavor.'),
    I(/\boregano\b/, 'An extract of oregano, used in small amounts.'),
    I(/\bgreen tea\b/, 'Green tea extract.', 'Green tea naturally contains some caffeine.'),
    I(/\bkombucha\b/, 'Dried kombucha, a fermented tea.'),
    I(/\b(lutein|lycopene|zeaxanthin)\b/, 'A plant pigment (a carotenoid), added as a nutrient.'),
    I(/\b(apple|carrot|cranberry|orange|broccoli|tomato|goji|beet|spinach|kale|banana|blueberry|strawberry|blackberry|cherry|pomegranate|acai|acerola)\b|\bberry\b|\bjuice\b/, 'A dried fruit or vegetable powder.'),
    I(/\bvitamin b12\b|cobalamin\b/, 'Added vitamin B12.', "Plant foods don't naturally provide B12, so it's a common addition to vegan products."),
    I(/\bchelate\b|\b(zinc|copper|manganese|magnesium|calcium|iron|selenium|chromium|potassium) (oxide|citrate|gluconate|sulfate|carbonate|phosphate|chloride)\b|\biodide\b|\b(molybdate|selenite|selenate|menaquinone|methylfolate)\b|\bpantothen|\bretinyl\b|\bvitamin\b|\bpalmitate\b|\bniacin(amide)?\b|\briboflavin\b|\bthiamin|\bpyridoxine\b|\bfolic acid\b|\bfolate\b|\bbiotin\b|\bascorbic\b|\bcholecalciferol\b/, 'An added vitamin or mineral, in a form that keeps well in a powder.', 'If you also take a multivitamin, check the nutrition panel so you know your total.'),
    I(/\bmethionine\b/, 'An essential amino acid, added to round out the amino acid profile of plant protein.'),
    I(/\btitanium dioxide\b/, 'A white color additive.', "The EU stopped allowing it in food in 2022. It's still permitted in the US."),
    I(/\bcaramel colou?r/, 'A brown color made by heating sugars.'),
    I(/\b(red|yellow|blue|green)\s*(no\.?\s*)?\d+\b|\bfd\s?&\s?c\b/, 'A synthetic color additive.', 'Certified colors must be named on the label. Some people choose to avoid synthetic dyes.'),
  ];
  const FALLBACK = {
    artificial: I(null, 'An artificial sweetener.', GROUPS.artificial.note),
    stevia: I(null, 'A stevia sweetener.', GROUPS.stevia.note),
    polyol: I(null, 'A sugar alcohol.', GROUPS.polyol.note),
    othersweet: I(null, 'A low-calorie sweetener.'),
    sugar: I(null, 'A sugar or syrup, for sweetness.', SUGAR_WATCH),
    monk: I(null, 'Monk fruit, for sweetness.', GROUPS.monk.note),
    dairy: I(null, 'A milk ingredient.', GROUPS.dairy.note),
    soy: I(null, 'A soy ingredient.', GROUPS.soy.note),
    allergen: I(null, 'A major food allergen.', GROUPS.allergen.note),
    legume: I(null, 'A legume ingredient.', LEGUME_WATCH),
    gum: I(null, 'A thickener, for texture.', GROUPS.gum.note),
    fiber: I(null, 'An added fiber.', GROUPS.fiber.note),
    coconut: I(null, 'A coconut ingredient.'),
    color: I(null, 'A color additive.', GROUPS.color.note),
    flavor: I(null, 'A flavoring.', FLAVOR_WATCH),
    filler: I(null, 'A starch or carbohydrate, for bulk or as a carrier.', GROUPS.filler.note),
  };
  const infoFor = (item) => {
    const norm = normalize(item.text.replace(/[([]\s*(comprised of|derived from|made from)\b[^)\]]*[)\]]/gi, ''));
    return INFO.find((info) => info.re.test(norm)) || (item.tags.length ? FALLBACK[item.tags[0]] : null);
  };

  const SAMPLES = {
    whey:
      'Ingredients: Protein Blend (Whey Protein Isolate, Whey Protein Concentrate, Milk Protein Concentrate), Natural and Artificial Flavors, Cellulose Gum, Xanthan Gum, Soy Lecithin, Salt, Sucralose, Acesulfame Potassium. Contains: Milk, Soy.',
    plant:
      'Ingredients: Organic Plant Protein Blend (Organic Pea Protein, Organic Brown Rice Protein, Organic Chia Seed Protein), Organic Erythritol, Organic Natural Flavors, Organic Inulin, Organic Acacia Gum, Organic Guar Gum, Sea Salt, Organic Stevia Leaf Extract, Xanthan Gum.',
    cantein:
      'Organic hemp protein, organic MCT powder (from coconut), organic vanilla powder, organic flavor (from vanilla), organic monk fruit, salt.',
  };

  const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
  const clean = (text) =>
    text
      .replace(/[*†‡®™]/g, '')
      .replace(/\s+/g, ' ')
      .replace(/^[\s,;:.]+|[\s,;:.]+$/g, '')
      .trim();
  const normalize = (text) =>
    ` ${text.toLowerCase().replace(/[*†‡®™]/g, '').replace(/\b(certified )?organic\b|\bnon[\s-]?gmo\b/g, ' ').replace(/\s+/g, ' ')} `;

  function splitTop(text) {
    const parts = [];
    let depth = 0;
    let current = '';
    for (let i = 0; i < text.length; i += 1) {
      const ch = text[i];
      if ('([{'.includes(ch)) depth += 1;
      if (')]}'.includes(ch)) depth = Math.max(0, depth - 1);
      const sentenceEnd = ch === '.' && depth === 0 && /\s/.test(text[i + 1] || ' ') && !/(^|[\s(,])[A-Za-z]$/.test(current);
      if (depth === 0 && (ch === ',' || ch === ';' || ch === '\n' || sentenceEnd)) {
        parts.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
    parts.push(current);
    return parts.map((part) => clean(clean(part).replace(/^(and\/or|and|or)\s+/i, ''))).filter((part) => part && !/^(and|or)$/i.test(part));
  }

  function parseItem(text) {
    const match = text.match(/^([^([{]*)[([{]([\s\S]*)[)\]}]\s*$/);
    if (!match) return { text, name: text, children: [] };
    return { text, name: clean(match[1]), children: splitTop(match[2]).map(parseItem) };
  }

  const DESCRIPTOR = /^(grass[\s-]?fed|undenatured|instantized|agglomerated|cold[\s-]?processed|non[\s-]?gmo|gmo[\s-]?free|organic|raw|native|[<>]?\s*\d[\d.,]*\s*(m?g|mcg|%))$/i;
  const SOURCE = /^(comprised of|derived from|made from|from)\b/i;

  function leaves(item) {
    if (!item.children.length) return [item.text];
    if (item.children.every((child) => DESCRIPTOR.test(child.text)) || SOURCE.test(item.children[0].text)) return [item.text];
    const isGroup =
      /\b(blend|matrix|complex|system|mix|proprietary|formula)\b/i.test(item.name) ||
      (item.children.length > 1 && !/\blecithin\b|\bflavou?rs?\b|\boil\b|\bmct\b|\bmedium[\s-]chain/i.test(item.name));
    return isGroup ? item.children.flatMap(leaves) : [item.text];
  }

  function analyze(raw) {
    let text = ` ${raw.replace(/\r/g, '')} `;
    const advisory = [];
    const statement = [];

    text = text.replace(/[*†‡]+\s*(certified\s+)?organic(\s+ingredients?)?\.?/gi, ' ');
    text = text.replace(/(?:^|[.\n])\s*([^.\n]*\b(?:may contain|facility|shared equipment|same equipment|traces? of)\b[^.\n]*)/gi, (match, sentence) => {
      advisory.push(clean(sentence));
      return '.';
    });
    text = text.replace(/\b(?:contains|allergens?)\s*:?\s*([^.\n]*)/gi, (match, list) => {
      if (/%|percent|less than|or less/i.test(list)) return match;
      const words = list.split(/,|&|\band\b/i).map(clean).filter(Boolean);
      if (!words.length || words.some((word) => word.replace(/\([^)]*\)/g, '').trim().split(' ').length > 3)) return match;
      statement.push(...words);
      return ' ';
    });
    text = text
      .replace(/\s*\((?:instantized|agglomerated)\)/gi, '')
      .replace(/(\([^()]*,[^()]*\))\s+(?=[a-z])/gi, '$1, ')
      .replace(/\b(?:other\s+)?ingredients\s*:/gi, ',')
      .replace(/(?:^|[,.])\s*[^,.:()]*?\b(?:blend|complex|matrix|system)\s*:/gi, ',')
      .replace(/\b(?:contains\s*)?(?:\d+(?:\.\d+)?\s*(?:%|percent)\s*or\s+less\s+of|less\s+than\s+\d+(?:\.\d+)?\s*(?:%|percent)\s+of)(?:\s+the\s+following)?\s*:?/gi, ',');

    const items = splitTop(text).map(parseItem).flatMap(leaves).map(clean).filter(Boolean);
    const hits = [];
    const listed = items.map((item) => {
      const norm = normalize(item);
      const tags = [];
      RULES.forEach((rule) => {
        if (!rule.re.test(norm) || (rule.not && rule.not.test(norm))) return;
        if (tags.includes(rule.group) && !rule.tag) return;
        tags.push(rule.group);
        hits.push({ group: rule.group, text: item, norm, tag: rule.tag });
      });
      return { text: item, tags };
    });

    const proteins = [];
    items.forEach((item) => {
      const norm = normalize(item);
      PROTEINS.forEach(([name, re]) => {
        if (re.test(norm) && !proteins.includes(name)) proteins.push(name);
      });
    });

    statement.forEach((word) => {
      const norm = normalize(word);
      STATEMENT_ALLERGENS.forEach(([group, re, tag]) => {
        if (!re.test(norm)) return;
        const already = hits.some((hit) => hit.group === group && (group !== 'allergen' || hit.tag === tag));
        if (!already) hits.push({ group, text: `${tag} (from the "Contains" statement)`, norm, tag: group === 'allergen' ? tag : undefined });
      });
    });

    return { items: listed, hits, proteins, statement, advisory };
  }

  function render(result, showCta, product) {
    const { items, hits, proteins, statement, advisory } = result;
    if (!items.length && !statement.length) {
      return '<p class="cantein-ic__empty">Paste an ingredient list above, then press Check ingredients.</p>';
    }

    const byGroup = {};
    hits.forEach((hit) => {
      (byGroup[hit.group] ||= []).push(hit);
    });
    const flagged = CHECKS.filter((check) => hits.some(check.test)).length;
    const count = items.length;

    const checks = CHECKS.map((check) => {
      const found = hits.filter(check.test).map((hit) => hit.text);
      const unique = [...new Set(found)];
      return `<li class="${unique.length ? 'is-found' : 'is-clear'}">
        <span class="cantein-ic__check-label">${esc(check.label)}</span>
        <strong>${unique.length ? 'Found' : 'None found'}</strong>
        ${unique.length ? `<small>${esc(unique.join(', '))}</small>` : ''}
      </li>`;
    }).join('');

    const groups = ORDER.filter((id) => byGroup[id])
      .map((id) => {
        const group = GROUPS[id];
        const names = [...new Set(byGroup[id].map((hit) => (hit.tag && id === 'allergen' ? `${hit.text} · ${hit.tag}` : hit.text)))];
        return `<section class="cantein-ic__group is-${group.tone}">
          <h3>${esc(group.title)}</h3>
          <ul>${names.map((name) => `<li>${esc(name)}</li>`).join('')}</ul>
          <p>${group.note}</p>
        </section>`;
      })
      .join('');

    const protein = proteins.length
      ? `Protein from: <strong>${esc(proteins.join(', '))}</strong>.`
      : 'No protein source recognized. Check that you pasted the full ingredient list.';

    const summary =
      flagged === 0
        ? `None of the ${CHECKS.length} things on the checklist below are on this label.`
        : `${flagged} of the ${CHECKS.length} things on the checklist below ${flagged === 1 ? 'is' : 'are'} on this label.`;

    const statementNote = statement.length
      ? `<p class="cantein-ic__statement">The label's allergen statement says it contains: <strong>${esc(statement.join(', '))}</strong>.</p>`
      : '';
    const advisoryNote = advisory.length
      ? `<p class="cantein-ic__statement">The label also says: "${esc(advisory.join(' '))}" Statements like this are voluntary and aren't in the ingredient count. If you have a food allergy, ask the manufacturer.</p>`
      : '';

    const list = `<section class="cantein-ic__list">
      <h3>Every ingredient: why it's used and what to know</h3>
      <p class="cantein-ic__list-intro">In label order, heaviest first, except for anything after "2% or less of."</p>
      <ol>${items
        .map((item) => {
          const info = infoFor(item);
          const tags = item.tags.length ? `<span class="cantein-ic__tags">${esc([...new Set(item.tags)].map((tag) => GROUPS[tag].title.toLowerCase()).join(' · '))}</span>` : '';
          const notes = info
            ? `<dl>
                <div><dt>Why it's used</dt><dd>${info.why}</dd></div>
                ${info.watch ? `<div><dt>Worth knowing</dt><dd>${info.watch}</dd></div>` : ''}
              </dl>`
            : '';
          return `<li><p class="cantein-ic__item-name"><strong>${esc(item.text)}</strong>${tags}</p>${notes}</li>`;
        })
        .join('')}</ol>
    </section>`;

    const productHead = product
      ? `<p class="cantein-ic__product">${esc(product.brand)} ${esc(product.name)}, ${esc(product.flavor)}</p>`
      : '';

    const bag = document.querySelectorAll('[data-ic-bag]')[0]?.dataset.icBag;
    const cta = showCta
      ? `<aside class="cantein-ic__cta">
          ${bag ? `<img class="cantein-ic__cta-img" src="${esc(bag)}" alt="Cantein organic unflavored hemp protein bag: one ingredient, 28g protein per 35g scoop" width="120" height="120" loading="lazy">` : ''}
          <p><strong>Cantein Unflavored is one ingredient: organic hemp protein.</strong> 28g of protein per scoop, and nothing on the checklist above. Vanilla and chocolate add organic MCT from coconut, flavor, monk fruit and salt.</p>
          <a class="cantein-ic__button" href="/collections/20-servings">Shop Cantein</a>
        </aside>`
      : '';

    return `${productHead}<div class="cantein-ic__head">
        <p class="cantein-ic__count"><strong>${count}</strong> ingredient${count === 1 ? '' : 's'}</p>
        <p class="cantein-ic__verdict">${esc(summary)} ${protein}</p>
      </div>
      <ul class="cantein-ic__checks">${checks}</ul>
      ${statementNote}${advisoryNote}
      ${list}
      ${groups ? `<h3 class="cantein-ic__groups-title">By category</h3><div class="cantein-ic__groups">${groups}</div>` : ''}
      <p class="cantein-ic__lab">A label can't show heavy metals or how a powder was tested. For that you need published lab results. ${a(LINK.lead, 'Lead in protein powder')}.</p>
      ${cta}`;
  }

  const PRODUCTS = Array.isArray(window.CanteinIngredientProducts) ? window.CanteinIngredientProducts : [];
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const formatDate = (iso) => {
    const [year, month, day] = iso.split('-').map(Number);
    return `${MONTHS[month - 1]} ${day}, ${year}`;
  };
  const productLabel = (product) => `${product.brand} ${product.name}, ${product.flavor}`;
  const searchText = (product) => `${product.brand} ${product.name} ${product.flavor} ${product.type}`.toLowerCase().replace(/,/g, ' ');
  const findProducts = (query) => {
    const words = query.toLowerCase().split(/[\s,]+/).filter(Boolean);
    return PRODUCTS.filter((product) => words.every((word) => searchText(product).includes(word)));
  };

  const VISIT_ID = window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  const MISS_DELAY = 2000;
  const trackedMisses = new Set();
  let missTimer = 0;

  // Anonymous, personless PostHog event: no cookies, no stored ID, no location, and never the pasted list.
  function track(key, event, properties) {
    if (!key) return;
    const privacy = window.Shopify?.customerPrivacy;
    if (typeof privacy?.analyticsProcessingAllowed === 'function' && !privacy.analyticsProcessingAllowed()) return;
    fetch('https://us.i.posthog.com/i/v0/e/', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        api_key: key,
        event,
        distinct_id: VISIT_ID,
        properties: {
          ...properties,
          tool: 'ingredient_checker',
          $process_person_profile: false,
          $geoip_disable: true,
          $current_url: location.origin + location.pathname,
        },
      }),
    }).catch(() => {});
  }

  function trackMiss(key, query) {
    clearTimeout(missTimer);
    const term = query.toLowerCase().replace(/\s+/g, ' ').trim();
    if (term.length < 3 || trackedMisses.has(term)) return;
    missTimer = setTimeout(() => {
      trackedMisses.add(term);
      track(key, 'product_search_no_results', { search_term: term.slice(0, 80) });
    }, MISS_DELAY);
  }

  document.querySelectorAll('[data-ic-app]').forEach((app, appIndex) => {
    const input = app.querySelector('[data-ic-input]');
    const results = app.querySelector('[data-ic-results]');
    const finder = app.querySelector('[data-ic-finder]');
    const find = app.querySelector('[data-ic-find]');
    const options = app.querySelector('[data-ic-options]');
    const source = app.querySelector('[data-ic-source]');
    const showCta = app.dataset.icCta === 'true';
    let timer = 0;
    let selected = null;
    let matches = [];
    let active = -1;

    const run = (scroll) => {
      const value = input.value.trim();
      if (!value) {
        results.hidden = true;
        results.innerHTML = '';
        return;
      }
      results.innerHTML = render(analyze(value), showCta, selected);
      results.hidden = false;
      if (scroll && results.getBoundingClientRect().top > window.innerHeight * 0.8) {
        results.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    const showSource = (product) => {
      if (!source) return;
      if (!product) {
        source.hidden = true;
        source.innerHTML = '';
        return;
      }
      const own = product.url.startsWith('/');
      const link = own
        ? a(product.url, 'our ingredients page')
        : `<a href="${esc(product.url)}" target="_blank" rel="nofollow noopener">${esc(product.brand)}'s product page</a>`;
      source.innerHTML = `Ingredients for <strong>${esc(productLabel(product))}</strong>, copied from ${link} on ${formatDate(product.checked)}. ${product.note ? `${esc(product.note)} ` : ''}Formulas change, so check the label on your tub.`;
      source.hidden = false;
    };

    const clearProduct = () => {
      selected = null;
      showSource(null);
    };

    const closeOptions = () => {
      if (!options) return;
      options.hidden = true;
      find.setAttribute('aria-expanded', 'false');
      find.removeAttribute('aria-activedescendant');
      active = -1;
    };

    const setActive = (index) => {
      const items = options.querySelectorAll('[role="option"]');
      if (!items.length) return;
      active = (index + items.length) % items.length;
      items.forEach((item, i) => item.setAttribute('aria-selected', i === active ? 'true' : 'false'));
      find.setAttribute('aria-activedescendant', items[active].id);
      items[active].scrollIntoView({ block: 'nearest' });
    };

    const openOptions = () => {
      matches = findProducts(find.value);
      if (matches.length || !find.value.trim()) clearTimeout(missTimer);
      else trackMiss(app.dataset.analyticsKey, find.value);
      options.innerHTML = matches.length
        ? matches
            .map(
              (product, i) => `<li role="option" id="ic-${appIndex}-opt-${i}" data-index="${i}" aria-selected="false">
                <strong>${esc(product.brand)}</strong> ${esc(product.name)}
                <span>${esc(product.flavor)} · ${esc(product.type)}</span>
              </li>`
            )
            .join('')
        : '<li class="cantein-ic__no-match">Not in our list yet. Paste the ingredient list below instead.</li>';
      options.hidden = false;
      find.setAttribute('aria-expanded', 'true');
      active = -1;
    };

    const choose = (product) => {
      selected = product;
      find.value = productLabel(product);
      input.value = product.ingredients;
      closeOptions();
      showSource(product);
      run(true);
    };

    if (finder && find && options && PRODUCTS.length) {
      finder.hidden = false;
      app.querySelectorAll('[data-ic-product-count]').forEach((node) => {
        node.textContent = PRODUCTS.length;
      });
      find.addEventListener('focus', openOptions);
      find.addEventListener('input', openOptions);
      find.addEventListener('blur', () => setTimeout(closeOptions, 150));
      find.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          if (options.hidden) openOptions();
          setActive(active + (event.key === 'ArrowDown' ? 1 : -1));
        } else if (event.key === 'Enter') {
          const pick = matches[active] || (matches.length === 1 ? matches[0] : null);
          if (pick) {
            event.preventDefault();
            choose(pick);
          }
        } else if (event.key === 'Escape') {
          closeOptions();
        }
      });
      options.addEventListener('mousedown', (event) => {
        const option = event.target.closest('[role="option"]');
        if (!option) return;
        event.preventDefault();
        choose(matches[Number(option.dataset.index)]);
      });
    }

    app.querySelector('[data-ic-run]').addEventListener('click', () => run(true));
    input.addEventListener('input', () => {
      if (selected && input.value !== selected.ingredients) {
        clearProduct();
        if (find) find.value = '';
      }
      clearTimeout(timer);
      timer = setTimeout(() => run(false), 350);
    });
    app.querySelectorAll('[data-ic-sample]').forEach((button) => {
      button.addEventListener('click', () => {
        clearProduct();
        if (find) find.value = '';
        input.value = SAMPLES[button.dataset.icSample] || '';
        run(true);
      });
    });
    app.querySelector('[data-ic-clear]').addEventListener('click', () => {
      clearProduct();
      if (find) find.value = '';
      input.value = '';
      run(false);
      input.focus();
    });
  });

  window.CanteinIngredientCheckerAnalyze = analyze;
})();
